import { useState, KeyboardEvent, MouseEvent, useEffect, useRef } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import useAlertStore from '@/stores/alertStore';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { useOrganizations, useOrganizationInvites } from '../hooks/useOrganizations';
import { BETA_ORGANIZATIONS_FEATURE_ENABLED } from '../config';
import { RouteMap } from '@/routes/Routes';
import { hasBetaProductName } from '@/utils/subscriptionHelpers';
import { useCurrentPlanFeatures } from '@/hooks/useCurrentPlanFeatures';
import AddMemberModal from '../components/AddMemberModal';
import PendingInvitesSection from '../components/PendingInvitesSection';
import UsageChart from '../components/UsageChart';
import TeamFoldersSection from '../components/TeamFoldersSection';
import SilentErrorBoundary from '@/components/SilentErrorBoundary';
import type { Organization } from '../types';

const MAX_TEAMS = 1;

interface AddMemberButtonProps {
  organization: Organization;
  defaultSeats: number | null;
  isMutating: boolean;
  onClick: () => void;
}

/**
 * Add Member button that disables itself when the org has reached its
 * plan's default_seats cap (members + pending invites). Shares the
 * useOrganizationInvites cache key with PendingInvitesSection on the
 * same org, so no extra fetch is incurred in practice.
 */
const AddMemberButton = ({ organization, defaultSeats, isMutating, onClick }: AddMemberButtonProps) => {
  const { data: invitesData } = useOrganizationInvites(organization.id);
  const pendingInviteCount = (invitesData?.invites ?? []).filter((i) => i.status === 'pending').length;
  const usedSeats = organization.member_emails.length + pendingInviteCount;
  const atSeatLimit = defaultSeats !== null && usedSeats >= defaultSeats;

  const button = (
    <Button
      variant="contained"
      size="small"
      startIcon={<PersonAddOutlinedIcon />}
      onClick={onClick}
      disabled={isMutating || atSeatLimit}
    >
      Add Member
    </Button>
  );

  if (!atSeatLimit) return button;
  return (
    <Tooltip title={`Seat limit reached (${defaultSeats}). Remove a member or revoke a pending invite first.`}>
      <span>{button}</span>
    </Tooltip>
  );
};

const deriveNameFromEmail = (email: string) => {
  const local = email.split('@')[0] ?? email;
  return local
    .split(/[._-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const OrganizationsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userData } = useAuthStore();
  const { betaFeatures, features, defaultSeats, isLoading: isPlanLoading } = useCurrentPlanFeatures();
  // Access is granted to anyone on a teams-included plan, existing beta testers,
  // or members already invited to a team (handled below via organizations.length).
  const canManageTeams = features.teamCollaboration || hasBetaProductName(userData) || !!betaFeatures.organizations;

  const showAlert = useAlertStore((state) => state.showAlert);
  const [newOrgName, setNewOrgName] = useState('');
  const [memberModalOrg, setMemberModalOrg] = useState<Organization | null>(null);
  const [deleteConfirmOrg, setDeleteConfirmOrg] = useState<{ id: string; name: string } | null>(null);
  const [memberMenu, setMemberMenu] = useState<{
    anchor: HTMLElement;
    orgId: string;
    email: string;
    isAdmin: boolean;
  } | null>(null);

  const {
    organizations,
    isLoading,
    isMutating,
    error,
    createOrganization,
    renameOrganization,
    deleteOrganization,
    removeMember,
    promoteAdmin,
    demoteAdmin,
  } = useOrganizations(BETA_ORGANIZATIONS_FEATURE_ENABLED);

  const [renamingOrgId, setRenamingOrgId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const renameInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (renamingOrgId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingOrgId]);

  const hasTeam = organizations.length >= MAX_TEAMS;
  const descriptionText =
    'Create a team to share notes with colleagues. Add members by email and they will have access to all notes shared with the team.';

  if (!BETA_ORGANIZATIONS_FEATURE_ENABLED) {
    return <Navigate to={RouteMap.HOME} replace />;
  }

  // Allow non-beta users to view teams they've been invited to.
  // Only redirect to /beta when the user has no organizations and is not a beta tester.
  // Guard on `!error` so a transient fetch failure doesn't bounce a real team member to /beta.
  // Also wait for `userData?.id` so we don't redirect while `useOrganizations` is still
  // gated on resolved identity (a disabled query reports isLoading=false + empty array).
  if (!canManageTeams && !isPlanLoading && !isLoading && !error && !!userData?.id && organizations.length === 0) {
    return <Navigate to={RouteMap.HOME} replace />;
  }

  const handleCreateOrg = async () => {
    if (!newOrgName.trim()) {
      return;
    }

    if (organizations.length >= MAX_TEAMS) {
      showAlert('You can only create one team.', 'info');
      return;
    }

    try {
      await createOrganization({ name: newOrgName.trim() });
      setNewOrgName('');
      showAlert('Team created.', 'success');
    } catch (error) {
      showAlert(getApiErrorMessage(error, 'Could not create team. Please try again.'), 'error');
    }
  };

  const handleKeyDown = async (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      await handleCreateOrg();
    }
  };

  const handleDelete = async (orgId: string) => {
    try {
      await deleteOrganization(orgId);
      showAlert('Team deleted.', 'success');
      setDeleteConfirmOrg(null);
    } catch (error) {
      showAlert(getApiErrorMessage(error, 'Could not delete team. Please try again.'), 'error');
      setDeleteConfirmOrg(null);
    }
  };

  const handleDeleteClick = (org: Organization) => {
    setDeleteConfirmOrg({ id: org.id, name: org.name });
  };

  const handleRemoveMember = async (orgId: string, email: string) => {
    try {
      await removeMember({ organizationId: orgId, email });
      showAlert('Member removed.', 'success');
    } catch (error) {
      showAlert(getApiErrorMessage(error, 'Could not remove member. Please try again.'), 'error');
    }
  };

  const handlePromoteAdmin = async (orgId: string, email: string) => {
    try {
      await promoteAdmin({ organizationId: orgId, email });
      showAlert('Member is now an admin.', 'success');
    } catch (error) {
      showAlert(getApiErrorMessage(error, 'Could not update admin. Please try again.'), 'error');
    }
  };

  const handleDemoteAdmin = async (orgId: string, email: string) => {
    try {
      await demoteAdmin({ organizationId: orgId, email });
      showAlert('Admin access removed.', 'success');
    } catch (error) {
      showAlert(getApiErrorMessage(error, 'Could not update admin. Please try again.'), 'error');
    }
  };

  const handleMemberMenuOpen = (event: MouseEvent<HTMLElement>, orgId: string, email: string, isAdmin: boolean) => {
    setMemberMenu({ anchor: event.currentTarget, orgId, email, isAdmin });
  };

  const handleMemberMenuClose = () => {
    setMemberMenu(null);
  };

  const handleRemoveMenuClick = () => {
    if (!memberMenu) return;
    const { orgId, email } = memberMenu;
    handleMemberMenuClose();
    handleRemoveMember(orgId, email);
  };

  const handleToggleAdminClick = () => {
    if (!memberMenu) return;
    const { orgId, email, isAdmin } = memberMenu;
    handleMemberMenuClose();
    if (isAdmin) {
      handleDemoteAdmin(orgId, email);
    } else {
      handlePromoteAdmin(orgId, email);
    }
  };

  const isOwner = (org: Organization) => org.owner_id === userData?.firebase_uid;
  // Admins are stored as lowercased emails on the org; the owner is always a manager.
  const currentEmail = (userData?.email ?? '').toLowerCase();
  const isAdminMember = (org: Organization) => (org.admin_emails ?? []).includes(currentEmail);
  const canManage = (org: Organization) => isOwner(org) || isAdminMember(org);

  const handleStartRename = (org: Organization) => {
    setRenamingOrgId(org.id);
    setRenameDraft(org.name);
  };

  const handleCancelRename = () => {
    setRenamingOrgId(null);
    setRenameDraft('');
  };

  const handleCommitRename = async (org: Organization) => {
    const trimmed = renameDraft.trim();
    if (!trimmed || trimmed === org.name) {
      handleCancelRename();
      return;
    }
    setIsRenaming(true);
    try {
      await renameOrganization({ organizationId: org.id, name: trimmed });
      showAlert('Team renamed.', 'success');
      setRenamingOrgId(null);
      setRenameDraft('');
    } catch (error) {
      showAlert(getApiErrorMessage(error, 'Could not rename team. Please try again.'), 'error');
    } finally {
      setIsRenaming(false);
    }
  };

  const handleRenameKeyDown = (event: KeyboardEvent<HTMLDivElement>, org: Organization) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleCommitRename(org);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleCancelRename();
    }
  };

  return (
    <Box
      px={isMobile ? 2 : 4}
      py={4}
      sx={{
        maxWidth: '1200px',
        margin: '0 auto',
      }}
    >
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Teams
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {descriptionText}
          </Typography>
        </Box>

        {!hasTeam && (
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight={600} display="flex" alignItems="center" gap={1}>
                  <GroupsOutlinedIcon fontSize="small" />
                  Create a team
                </Typography>
                <TextField
                  label="Team name"
                  placeholder="e.g. Marketing Team"
                  value={newOrgName}
                  onChange={(event) => setNewOrgName(event.target.value)}
                  onKeyDown={handleKeyDown}
                  fullWidth
                  disabled={isMutating}
                  helperText="Create a team to share notes with colleagues."
                />
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    onClick={handleCreateOrg}
                    disabled={isMutating || !newOrgName.trim()}
                    startIcon={isMutating ? <CircularProgress size={16} /> : <GroupsOutlinedIcon />}
                  >
                    Create team
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        )}

        {isLoading ? (
          <Stack alignItems="center" justifyContent="center" py={6}>
            <CircularProgress />
          </Stack>
        ) : organizations.length === 0 ? (
          <Alert severity="info">
            Create your team to start sharing notes with colleagues. Add members by email and share notes directly with
            the team.
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {organizations.map((org) => (
              <Grid item xs={12} key={org.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        {isOwner(org) && renamingOrgId === org.id ? (
                          <TextField
                            inputRef={renameInputRef}
                            value={renameDraft}
                            onChange={(event) => setRenameDraft(event.target.value)}
                            onBlur={() => handleCommitRename(org)}
                            onKeyDown={(event) => handleRenameKeyDown(event, org)}
                            size="small"
                            variant="standard"
                            disabled={isRenaming}
                            inputProps={{ maxLength: 200, 'aria-label': `Rename ${org.name}` }}
                            sx={{
                              flex: 1,
                              mr: 1,
                              '& .MuiInputBase-input': { fontSize: '1.25rem', fontWeight: 600 },
                            }}
                          />
                        ) : isOwner(org) ? (
                          <Typography
                            variant="h6"
                            fontWeight={600}
                            sx={{ cursor: 'text', '&:hover': { color: 'primary.main' } }}
                            onClick={() => handleStartRename(org)}
                            title="Click to rename"
                          >
                            {org.name}
                          </Typography>
                        ) : (
                          <Typography
                            variant="h6"
                            fontWeight={600}
                            sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                            onClick={() => navigate(`${RouteMap.ORGANIZATIONS}/${org.id}`)}
                          >
                            {org.name}
                          </Typography>
                        )}
                        {isOwner(org) && (
                          <IconButton
                            aria-label={`Delete ${org.name}`}
                            onClick={() => handleDeleteClick(org)}
                            size="small"
                            disabled={isMutating}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>

                      <Stack direction="row" spacing={2}>
                        <Chip
                          icon={<PersonAddOutlinedIcon />}
                          label={`${org.member_emails.length} member${org.member_emails.length !== 1 ? 's' : ''}`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          icon={<ArticleOutlinedIcon />}
                          label={`${org.note_ids.length} note${org.note_ids.length !== 1 ? 's' : ''}`}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>

                      <Box>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{
                            px: 1.5,
                            py: 1,
                            borderBottom: 1,
                            borderColor: 'divider',
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                          >
                            Name
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mr: canManage(org) ? 5 : 0 }}
                          >
                            Role
                          </Typography>
                        </Stack>
                        <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
                          {org.member_emails.map((email) => {
                            const memberEmail = email.toLowerCase();
                            const isOwnerEmail = memberEmail === (org.owner_email ?? '').toLowerCase();
                            // admin_emails is stored lowercased — comparing the raw
                            // member email made mixed-case admins render as "Member".
                            const memberIsAdmin = (org.admin_emails ?? []).includes(memberEmail);
                            // "(You)" marks the viewer, not the owner. Keyed on
                            // owner_email it appeared next to the owner for everyone.
                            const isCurrentUser = memberEmail === currentEmail;
                            const showMenu = canManage(org) && !isOwnerEmail;
                            return (
                              <Stack
                                key={email}
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                                sx={{ px: 1.5, py: 1.25, minWidth: 0 }}
                              >
                                <Avatar
                                  sx={{
                                    bgcolor: isOwnerEmail ? 'primary.main' : 'grey.500',
                                    width: 36,
                                    height: 36,
                                    fontSize: '0.95rem',
                                  }}
                                >
                                  {email.charAt(0).toUpperCase()}
                                </Avatar>
                                <Stack sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
                                    {deriveNameFromEmail(email)}
                                    {isCurrentUser && (
                                      <Typography
                                        component="span"
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ ml: 0.75 }}
                                      >
                                        (You)
                                      </Typography>
                                    )}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {email}
                                  </Typography>
                                </Stack>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ minWidth: 80, textAlign: 'right' }}
                                >
                                  {isOwnerEmail ? 'Owner' : memberIsAdmin ? 'Admin' : 'Member'}
                                </Typography>
                                {showMenu ? (
                                  <IconButton
                                    aria-label={`Member actions for ${email}`}
                                    size="small"
                                    onClick={(event) => handleMemberMenuOpen(event, org.id, email, memberIsAdmin)}
                                    disabled={isMutating}
                                  >
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                ) : (
                                  canManage(org) && <Box sx={{ width: 34 }} />
                                )}
                              </Stack>
                            );
                          })}
                        </Stack>
                        {canManage(org) && <PendingInvitesSection organizationId={org.id} />}
                      </Box>

                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
                        {canManage(org) && (
                          <AddMemberButton
                            organization={org}
                            defaultSeats={defaultSeats}
                            isMutating={isMutating}
                            onClick={() => setMemberModalOrg(org)}
                          />
                        )}
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => navigate(`${RouteMap.ORGANIZATIONS}/${org.id}`)}
                        >
                          View Notes
                        </Button>
                      </Stack>

                      {canManage(org) && (
                        <SilentErrorBoundary operation="organizationTeamFolders">
                          <TeamFoldersSection
                            organizationId={org.id}
                            ownerEmail={org.owner_email}
                            memberEmails={org.member_emails}
                          />
                        </SilentErrorBoundary>
                      )}

                      {canManage(org) && (
                        <SilentErrorBoundary operation="organizationUsageChart">
                          <UsageChart
                            organizationId={org.id}
                            ownerEmail={org.owner_email}
                            memberEmails={org.member_emails}
                          />
                        </SilentErrorBoundary>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>

      <AddMemberModal open={!!memberModalOrg} organization={memberModalOrg} onClose={() => setMemberModalOrg(null)} />

      <Menu anchorEl={memberMenu?.anchor ?? null} open={!!memberMenu} onClose={handleMemberMenuClose}>
        <MenuItem onClick={handleToggleAdminClick}>{memberMenu?.isAdmin ? 'Remove admin' : 'Make admin'}</MenuItem>
        <MenuItem onClick={handleRemoveMenuClick}>Remove from team</MenuItem>
      </Menu>

      <Dialog
        open={!!deleteConfirmOrg}
        onClose={() => setDeleteConfirmOrg(null)}
        aria-labelledby="delete-team-dialog-title"
        aria-describedby="delete-team-dialog-description"
      >
        <DialogTitle id="delete-team-dialog-title">Delete Team?</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-team-dialog-description">
            Are you sure you want to delete "{deleteConfirmOrg?.name}"? This action cannot be undone and will affect all
            members and notes associated with this team.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOrg(null)} disabled={isMutating}>
            Cancel
          </Button>
          <Button
            onClick={() => deleteConfirmOrg && handleDelete(deleteConfirmOrg.id)}
            color="error"
            variant="contained"
            disabled={isMutating}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationsPage;
